import React, { useState, useMemo, useEffect } from 'react';
import './Documents.css';
import { FaPlus, FaChevronDown, FaSearch } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useDocumentDate } from './context/DateContext.js';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;
const ADMIN_BASE_URL = process.env.REACT_APP_EZTS_ADMIN_BASE_URL;

const formatSize = (size) => {
  if (size > 1024 * 1024) return (size / (1024 * 1024)).toFixed(2) + ' MB';
  if (size > 1024) return (size / 1024).toFixed(2) + ' KB';
  return size + ' B';
};

const Documents = () => {
  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'descending' });
  const [searchInputs, setSearchInputs] = useState({});
  const [searchVisible, setSearchVisible] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const [tsDocError, setDocApiError] = useState(false);

  const { DocStartDate, 
          DocSetStartDate, 
          DocEndDate, 
          DocSetEndDate,
          DocHasFetched, 
          DocSetHasFetched } = useDocumentDate();
  
  const departments = {
    1: 'Finance',
    2: 'Marketing',
    3: 'Operations',
    4: 'Human Resources',
    5: 'Infomation Technology',
    6: 'Customer Service',
    7: 'Risk Management',
  };

  const fetchDocuments = async (from = "", to = "") => {
    if (!token || !user) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}document/all?orgId=${user.department?.costCenter?.organization?.orgId}&from=${from}&to=${to}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const apiData = response.data || [];
      console.log("Document API response:", apiData);

      const mapped = apiData.map(doc => ({
        doc_id: doc.docId || "",
        dept_name: doc.department?.deptName || "",
        file_name: doc.fileName || "",
        file_type: doc.fileType || "",
        file_size: doc.fileSize || -1,
        doc_date: doc.docDate || "",
        create_uid: doc.createUid || "",
        create_ts: doc.createTs || "",
        file_url: doc.fileUrl || "",
      }));
      

      setDocuments(mapped);
      setFilteredDocuments(null);
      DocSetHasFetched(true);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocApiError(true);
      setTimeout(() => setDocApiError(false), 1500);
    }
  }; 

  const sortedDocuments = useMemo(() => {
    let sortableItems = filteredDocuments ? [...filteredDocuments] : [...documents];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === 'file_size') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }
        if (sortConfig.key === 'doc_date') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [documents, filteredDocuments, sortConfig]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedDocuments.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedDocuments.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleAdd = () => {
    navigate('/documents/add');
  };

  const buildDownloadUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    // If the API returns a relative path, prefix with the API base URL.
    const base = ADMIN_BASE_URL || '';
    return `${base.replace(/\/+$/, '')}/${trimmed.replace(/^\/+/, '')}`;
  };

  /*
  const handleDownload = async (doc) => {
    if (!doc.file_url) return;

    const downloadUrl = buildDownloadUrl(doc.file_url);
    console.debug('Downloading document from:', downloadUrl);

    //window.open(downloadUrl, '_blank', 'noopener,noreferrer') // Opens file url

    try {
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    }
  };
*/

  const handleDownload = async (e, url) => {
    e.preventDefault();

    try {
      const response = await fetch(url);
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "file"; // customize filename if needed
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleApplyFilter = () => {
    const from = DocStartDate ? DocStartDate.toISOString().split("T")[0] : "";
    const to = DocEndDate ? DocEndDate.toISOString().split("T")[0] : "";
    fetchDocuments(from, to);
  };

  useEffect(() => {
    if (DocHasFetched) {
      handleApplyFilter();
    }
  }, []);


  const requestSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <span className="table-sort-arrows">
          <FaChevronDown className="table-sort-arrow up faded margin-bottom" />
          <FaChevronDown className="table-sort-arrow faded margin-top" />
        </span>
      );
    }
    const isAsc = sortConfig.direction === 'ascending';
    return (
      <FaChevronDown
        className={`table-sort-arrow centered${isAsc ? ' up' : ''}`}
      />
    );
  };

  const handleSearchIconClick = (key) => {
    setSearchVisible((prev) => {
      const allClosed = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {});
      return { ...allClosed, [key]: !prev[key] };
    });
  };

  const handleSearchInputChange = (key, value) => {
    setSearchInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchInputKeyDown = (key, e) => {
    if (e.key === 'Enter') {
      handleSearch(key);
    } else if (e.key === 'Escape') {
      setSearchVisible((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSearch = (key) => {
    const value = searchInputs[key] || '';
    setFilteredDocuments(
      documents.filter(document =>
        String(document[key] || '').toLowerCase().includes(value.toLowerCase())
      )
    );
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
    setCurrentPage(1);
  };

  const handleCloseSearch = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const renderSortableHeader = (key, title) => (
    <th className="document-table-header sortable">
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span onClick={() => requestSort(key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          {title} {getSortIcon(key)}
        </span>
        <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {searchVisible[key] && (
            <div className="table-search-popover">
              <input
                type="text"
                className="table-search-input"
                value={searchInputs[key] || ''}
                autoFocus
                onChange={e => handleSearchInputChange(key, e.target.value)}
                onKeyDown={e => handleSearchInputKeyDown(key, e)}
                placeholder={`Search ${title}...`}
              />
              <div className="table-search-btn-row">
                <button className="table-search-btn" onClick={() => handleSearch(key)}>
                  <FaSearch style={{ fontSize: '16px' }} />
                </button>
                <button className="table-search-close-btn" onClick={() => handleCloseSearch(key)}>
                  <IoMdClose style={{ fontSize: '18px' }} />
                </button>
              </div>
            </div>
          )}
          <FaSearch
            className="table-search-icon"
            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '14px' }}
            onClick={() => handleSearchIconClick(key)}
          />
        </span>
      </span>
    </th>
  );

  return (
    <div className="document-sheets-container">
      <div className="document-sheets-header">
        <h1 className="document-sheets-title">Document Sheets</h1>
      </div>

      <div className="filters-wrapper">
        <div className="filters-container">
          <div className="filter-controls">
            <div className="filter-group">
              <label className="filter-label">From:</label>
              <DatePicker
                selected={DocStartDate}
                onChange={date => DocSetStartDate(date)}
                selectsStart
                startDate={DocStartDate}
                endDate={DocEndDate}
                placeholderText="Select start date"
                className="date-input"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label" style={{ marginLeft: '24px' }}>To:</label>
              <DatePicker
                selected={DocEndDate}
                onChange={date => DocSetEndDate(date)}
                selectsEnd
                startDate={DocStartDate}
                endDate={DocEndDate}
                minDate={DocStartDate}
                placeholderText="Select end date"
                className="date-input"
              />
            </div>
            <button className="apply-filter-button" onClick={() => handleApplyFilter()}>
              Search
            </button>
          </div>
        </div>

        <div className="add-button-container">
          <button className="add-button" onClick={handleAdd}>
            <FaPlus />
            Add Document
          </button>
        </div>
      </div>

      <div className="document-table-container">
        <table className="document-table">
          <thead>
            <tr>
              {renderSortableHeader('doc_id', 'Document ID')}
              {renderSortableHeader('dept_name', 'Department')}
              {renderSortableHeader('file_name', 'File Name')}
              {renderSortableHeader('file_type', 'File Type')}
              {renderSortableHeader('file_size', 'File Size')}
              {renderSortableHeader('doc_date', 'Document Date')}
              {renderSortableHeader('create_uid', 'Username')}
              {renderSortableHeader('create_ts', 'Create Time')}
              <th className="document-table-header">Operation</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((doc) => {
              const downloadUrl = buildDownloadUrl(doc.file_url);
              return (
                <tr key={doc.doc_id}>
                  <td className="document-table-cell">{doc.doc_id}</td>
                  <td className="document-table-cell">{doc.dept_name}</td>
                  <td className="document-table-cell">{doc.file_name}</td>
                  <td className="document-table-cell">{doc.file_type}</td>
                  <td className="document-table-cell">{formatSize(doc.file_size)}</td>
                  <td className="document-table-cell">{doc.doc_date}</td>
                  <td className="document-table-cell">{doc.create_uid}</td>
                  <td className="document-table-cell">{doc.create_ts}</td>
                  <td className="document-table-cell">
                  {/*  
                  <button
                    type="button"
                    className="document-action-button download"
                    onClick={() => handleDownload(doc)}
                    disabled={!doc.file_url}
                  >
                    Download
                  </button>
                  */}
                  <a
                    href={downloadUrl}
                    className="document-action-button download"
                    //target="_blank"
                    //rel="noopener noreferrer"
                    onClick={(e) => handleDownload(e, downloadUrl)}
                  >
                    Download
                  </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button 
          onClick={handlePreviousPage} 
          disabled={currentPage === 1}
          className="pagination-arrow"
          aria-label="Previous page"
        >
          &lt;
        </button>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={currentPage}
          onChange={e => {
            let page = Number(e.target.value);
            if (page < 1) page = 1;
            if (page > totalPages) page = totalPages;
            setCurrentPage(page);
          }}
          className="pagination-input"
        />
        <span className="pagination-total">
          of {totalPages}
        </span>
        <button 
          onClick={handleNextPage} 
          disabled={currentPage === totalPages}
          className="pagination-arrow"
          aria-label="Next page"
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default Documents;